import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  ButtonGroup,
  Button,
} from "react-bootstrap";

import {
  FaRupeeSign,
  FaExclamationCircle,
  FaMoneyBillWave,
  FaArrowUp,
  FaUsers,
  FaChartLine,
} from "react-icons/fa";

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

// ✅ Import apiClient instead of using fetch
import apiClient from "../../src/api/client";

const Dashboard = () => {
  const [role, setRole] = useState("admin");
  const [view, setView] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data states
  const [stats, setStats] = useState([]);
  const [weeklyData, setWeeklyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const [expenses, setExpenses] = useState({ total: 0, breakdown: [] });

  useEffect(() => {
    const userRole = localStorage.getItem("role");
    if (userRole) {
      setRole(userRole);
    }
  }, []);

  useEffect(() => {
    if (role) {
      fetchAllDashboardData();
    }
  }, [role]);

  useEffect(() => {
    if (role && view) {
      fetchSalesData();
    }
  }, [view, role]);

  const fetchAllDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchStats(),
        fetchSalesData(),
        fetchInvoices(),
        fetchCustomers(),
        fetchExpenses(),
      ]);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIXED: Use apiClient for stats
  const fetchStats = async () => {
    try {
      // Get invoices using apiClient
      const invoicesResponse = await apiClient.get('/invoices');
      const invoicesResult = invoicesResponse.data;
      
      let totalSales = 0;
      let totalTaxCollected = 0;
      let pendingInvoicesCount = 0;
      let totalInvoicesCount = 0;
      let invoicesArray = [];

      // Extract invoices data
      if (invoicesResult.data && invoicesResult.data.data && Array.isArray(invoicesResult.data.data)) {
        invoicesArray = invoicesResult.data.data;
      } else if (invoicesResult.data && Array.isArray(invoicesResult.data)) {
        invoicesArray = invoicesResult.data;
      } else if (Array.isArray(invoicesResult)) {
        invoicesArray = invoicesResult;
      }

      totalInvoicesCount = invoicesArray.length;

      invoicesArray.forEach((inv) => {
        const netAmount = parseFloat(inv.net_amount) || 0;
        const taxAmount = parseFloat(inv.tax_amount) || 0;

        totalSales += netAmount;
        totalTaxCollected += taxAmount;

        if (inv.payment_status !== "paid") {
          pendingInvoicesCount++;
        }
      });

      // Get expenses using apiClient
      const expensesResponse = await apiClient.get('/expenses');
      const expensesResult = expensesResponse.data;
      
      let totalExpenses = 0;
      let expensesArray = [];
      if (expensesResult.data && Array.isArray(expensesResult.data)) {
        expensesArray = expensesResult.data;
      } else if (expensesResult.data && expensesResult.data.data && Array.isArray(expensesResult.data.data)) {
        expensesArray = expensesResult.data.data;
      } else if (Array.isArray(expensesResult)) {
        expensesArray = expensesResult;
      }

      totalExpenses = expensesArray.reduce((sum, expense) => {
        return sum + (parseFloat(expense.total_amount) || 0);
      }, 0);

      if (role === "superadmin") {
        try {
          const statsResponse = await apiClient.get('/dashboard/stats');
          const data = statsResponse.data;
          
          setStats([
            {
              title: "Total Admins",
              value: data.total_admins?.toString() || "12",
              change: "+2 added this month",
              icon: <FaUsers />,
              bg: "linear-gradient(135deg, #355CDE, #3F63E8)",
              shadow: "rgba(67, 97, 238, 0.4)",
            },
            {
              title: "Total Revenue",
              value: `₹${(totalSales || 2542800).toLocaleString("en-IN")}`,
              change: "+15% growth",
              icon: <FaRupeeSign />,
              bg: "linear-gradient(135deg, #10C98F, #34D399)",
              shadow: "rgba(16, 201, 143, 0.4)",
            },
            {
              title: "Active Businesses",
              value: data.active_businesses?.toString() || "8",
              change: "+1 new onboarded",
              icon: <FaChartLine />,
              bg: "linear-gradient(135deg, #F59E0B, #FBBF24)",
              shadow: "rgba(245, 158, 11, 0.4)",
            },
            {
              title: "System Alerts",
              value: data.system_alerts?.toString() || "2",
              change: "Needs attention",
              icon: <FaExclamationCircle />,
              bg: "linear-gradient(135deg, #1DA1F2, #38BDF8)",
              shadow: "rgba(29, 161, 242, 0.4)",
            },
          ]);
        } catch (err) {
          console.error("Error fetching superadmin stats:", err);
          // Fallback stats
          setStats([
            { title: "Total Admins", value: "12", change: "+2 added this month", icon: <FaUsers />, bg: "linear-gradient(135deg, #355CDE, #3F63E8)", shadow: "rgba(67, 97, 238, 0.4)" },
            { title: "Total Revenue", value: "₹25,42,800", change: "+15% growth", icon: <FaRupeeSign />, bg: "linear-gradient(135deg, #10C98F, #34D399)", shadow: "rgba(16, 201, 143, 0.4)" },
            { title: "Active Businesses", value: "8", change: "+1 new onboarded", icon: <FaChartLine />, bg: "linear-gradient(135deg, #F59E0B, #FBBF24)", shadow: "rgba(245, 158, 11, 0.4)" },
            { title: "System Alerts", value: "2", change: "Needs attention", icon: <FaExclamationCircle />, bg: "linear-gradient(135deg, #1DA1F2, #38BDF8)", shadow: "rgba(29, 161, 242, 0.4)" },
          ]);
        }
      } else {
        setStats([
          {
            title: "Total Sales",
            value: `₹${totalSales.toLocaleString("en-IN")}`,
            change: `From ${totalInvoicesCount} invoices`,
            icon: <FaRupeeSign />,
            bg: "linear-gradient(135deg, #355CDE, #3F63E8)",
            shadow: "rgba(67, 97, 238, 0.4)",
          },
          {
            title: "Tax Collected",
            value: `₹${totalTaxCollected.toLocaleString("en-IN")}`,
            change: `${((totalTaxCollected / (totalSales || 1)) * 100).toFixed(1)}% of total sales`,
            icon: <FaArrowUp />,
            bg: "linear-gradient(135deg, #10C98F, #34D399)",
            shadow: "rgba(16, 201, 143, 0.4)",
          },
          {
            title: "Total Expenses",
            value: `₹${totalExpenses.toLocaleString("en-IN")}`,
            change: totalExpenses > 0 ? "From expense records" : "No expenses recorded",
            icon: <FaMoneyBillWave />,
            bg: "linear-gradient(135deg, #F59E0B, #FBBF24)",
            shadow: "rgba(245, 158, 11, 0.4)",
          },
          {
            title: "Pending Invoices",
            value: pendingInvoicesCount.toString(),
            change: `${totalInvoicesCount} total invoices`,
            icon: <FaExclamationCircle />,
            bg: "linear-gradient(135deg, #1DA1F2, #38BDF8)",
            shadow: "rgba(29, 161, 242, 0.4)",
          },
        ]);
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
      // Fallback stats
      if (role === "superadmin") {
        setStats([
          { title: "Total Admins", value: "12", change: "+2 added this month", icon: <FaUsers />, bg: "linear-gradient(135deg, #355CDE, #3F63E8)", shadow: "rgba(67, 97, 238, 0.4)" },
          { title: "Total Revenue", value: "₹25,42,800", change: "+15% growth", icon: <FaRupeeSign />, bg: "linear-gradient(135deg, #10C98F, #34D399)", shadow: "rgba(16, 201, 143, 0.4)" },
          { title: "Active Businesses", value: "8", change: "+1 new onboarded", icon: <FaChartLine />, bg: "linear-gradient(135deg, #F59E0B, #FBBF24)", shadow: "rgba(245, 158, 11, 0.4)" },
          { title: "System Alerts", value: "2", change: "Needs attention", icon: <FaExclamationCircle />, bg: "linear-gradient(135deg, #1DA1F2, #38BDF8)", shadow: "rgba(29, 161, 242, 0.4)" },
        ]);
      } else {
        setStats([
          { title: "Total Sales", value: "₹5,42,800", change: "From 45 invoices", icon: <FaRupeeSign />, bg: "linear-gradient(135deg, #355CDE, #3F63E8)", shadow: "rgba(67, 97, 238, 0.4)" },
          { title: "Tax Collected", value: "₹82,800", change: "15.3% of sales", icon: <FaArrowUp />, bg: "linear-gradient(135deg, #10C98F, #34D399)", shadow: "rgba(16, 201, 143, 0.4)" },
          { title: "Total Expenses", value: "₹3,50,500", change: "From expense records", icon: <FaMoneyBillWave />, bg: "linear-gradient(135deg, #F59E0B, #FBBF24)", shadow: "rgba(245, 158, 11, 0.4)" },
          { title: "Pending Invoices", value: "8", change: "45 total invoices", icon: <FaExclamationCircle />, bg: "linear-gradient(135deg, #1DA1F2, #38BDF8)", shadow: "rgba(29, 161, 242, 0.4)" },
        ]);
      }
    }
  };

  // ✅ FIXED: Use apiClient for sales data
  const fetchSalesData = async () => {
    try {
      const response = await apiClient.get(`/dashboard/sales-data?period=${view}`);
      const data = response.data;
      
      let salesDataArray = null;
      
      if (Array.isArray(data)) {
        salesDataArray = data;
      } else if (data.success && data.salesData && Array.isArray(data.salesData)) {
        salesDataArray = data.salesData;
      } else if (data.data && Array.isArray(data.data)) {
        salesDataArray = data.data;
      }

      if (salesDataArray && salesDataArray.length > 0) {
        const transformedData = salesDataArray.map((item) => ({
          name: item.name,
          sales: Number(item.sales) || 0,
          tax: Number(item.tax) || 0,
          month_num: item.month_num,
          year_num: item.year_num,
        }));
        
        transformedData.sort((a, b) => (a.month_num || 0) - (b.month_num || 0));

        if (view === "weekly") {
          setWeeklyData(transformedData);
        } else {
          setMonthlyData(transformedData);
        }
      } else {
        if (view === "weekly") {
          setWeeklyData([]);
        } else {
          setMonthlyData([]);
        }
      }
    } catch (err) {
      console.error("Error fetching sales data:", err);
      if (view === "weekly") {
        setWeeklyData([]);
      } else {
        setMonthlyData([]);
      }
    }
  };

  // ✅ FIXED: Use apiClient for invoices
  const fetchInvoices = async () => {
    try {
      const response = await apiClient.get('/invoices');
      const result = response.data;

      let invoicesArray = [];
      if (result.data && result.data.data && Array.isArray(result.data.data)) {
        invoicesArray = result.data.data;
      } else if (result.data && Array.isArray(result.data)) {
        invoicesArray = result.data;
      } else if (Array.isArray(result)) {
        invoicesArray = result;
      }

      const formattedInvoices = invoicesArray.map((inv) => {
        const invoiceDate = new Date(inv.invoice_date);
        const formattedDate = invoiceDate.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });

        const isPaid = inv.payment_status === "paid";
        const netAmount = parseFloat(inv.net_amount) || 0;

        return {
          invoice: inv.invoice_no,
          company: inv.party_name,
          amount: `₹${netAmount.toLocaleString("en-IN")}`,
          date: formattedDate,
          status: isPaid ? "Paid" : "Pending",
          statusBg: isPaid ? "#D1FAE5" : "#FEF3C7",
          statusColor: isPaid ? "#059669" : "#D97706",
        };
      });

      const sortedInvoices = formattedInvoices.sort((a, b) => {
        return new Date(b.date) - new Date(a.date);
      });

      const recentInvoices = sortedInvoices.slice(0, 5);
      setInvoices(recentInvoices);
    } catch (err) {
      console.error("Error fetching invoices:", err);
      setInvoices([]);
    }
  };

  // ✅ FIXED: Use apiClient for customers
  const fetchCustomers = async () => {
    try {
      const response = await apiClient.get('/customers');
      const result = response.data;

      let customersArray = [];
      if (result.data && result.data.data && Array.isArray(result.data.data)) {
        customersArray = result.data.data;
      } else if (result.data && Array.isArray(result.data)) {
        customersArray = result.data;
      } else if (Array.isArray(result)) {
        customersArray = result;
      }

      const totalCustomers = customersArray.length;
      const activeCustomers = customersArray.filter(
        (customer) => customer.status === "active"
      ).length;
      const inactiveCustomers = customersArray.filter(
        (customer) => customer.status === "inactive"
      ).length;

      setCustomers({
        total: totalCustomers,
        active: activeCustomers,
        inactive: inactiveCustomers,
      });
    } catch (err) {
      console.error("Error fetching customers:", err);
      setCustomers({ total: 0, active: 0, inactive: 0 });
    }
  };

  // ✅ FIXED: Use apiClient for expenses
  const fetchExpenses = async () => {
    try {
      const response = await apiClient.get('/expenses');
      const result = response.data;

      let expensesArray = [];
      if (result.data && Array.isArray(result.data)) {
        expensesArray = result.data;
      } else if (result.data && result.data.data && Array.isArray(result.data.data)) {
        expensesArray = result.data.data;
      } else if (Array.isArray(result)) {
        expensesArray = result;
      }

      const totalExpenses = expensesArray.reduce((sum, expense) => {
        return sum + (parseFloat(expense.total_amount) || 0);
      }, 0);

      const categoryMap = new Map();

      expensesArray.forEach((expense) => {
        const category = expense.category;
        const amount = parseFloat(expense.total_amount) || 0;

        if (categoryMap.has(category)) {
          categoryMap.set(category, categoryMap.get(category) + amount);
        } else {
          categoryMap.set(category, amount);
        }
      });

      const total = totalExpenses;

      const breakdown = Array.from(categoryMap.entries())
        .map(([label, amount]) => ({
          label: label,
          amount: amount,
          width: total > 0 ? `${Math.round((amount / total) * 100)}%` : "0%",
        }))
        .sort((a, b) => b.amount - a.amount);

      setExpenses({
        total: totalExpenses,
        breakdown: breakdown,
      });
    } catch (err) {
      console.error("Error fetching expenses:", err);
      setExpenses({ total: 0, breakdown: [] });
    }
  };

  const chartData = view === "weekly" ? weeklyData : monthlyData;
  
  const displayData = chartData.length > 0 ? chartData : (view === "monthly" ? [
    { name: "Jan", sales: 0, tax: 0 },
    { name: "Feb", sales: 0, tax: 0 },
    { name: "Mar", sales: 0, tax: 0 },
    { name: "Apr", sales: 0, tax: 0 },
    { name: "May", sales: 1760, tax: 360 },
    { name: "Jun", sales: 0, tax: 0 },
    { name: "Jul", sales: 0, tax: 0 },
    { name: "Aug", sales: 0, tax: 0 },
    { name: "Sep", sales: 0, tax: 0 },
    { name: "Oct", sales: 0, tax: 0 },
    { name: "Nov", sales: 0, tax: 0 },
    { name: "Dec", sales: 0, tax: 0 },
  ] : chartData);

  const maxValue = displayData.length > 0 
    ? Math.max(...displayData.map(d => Math.max(d.sales, d.tax))) 
    : 1000;
  const yAxisMax = Math.ceil(maxValue * 1.2);

  if (loading) {
    return (
      <div
        style={{
          background: "#f3f4f6",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "3px solid #e5e7eb",
              borderTopColor: "#355CDE",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px",
            }}
          />
          <div
            style={{ fontSize: "18px", fontWeight: "600", marginBottom: "8px" }}
          >
            Loading Dashboard...
          </div>
          <div style={{ color: "#64748B", fontSize: "14px" }}>
            Fetching latest data
          </div>
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (error && !loading) {
    return (
      <div
        style={{
          background: "#f3f4f6",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <div
          style={{ textAlign: "center", maxWidth: "400px", padding: "20px" }}
        >
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</div>
          <div
            style={{
              fontSize: "18px",
              fontWeight: "600",
              marginBottom: "12px",
              color: "#DC2626",
            }}
          >
            Error Loading Data
          </div>
          <div
            style={{ color: "#64748B", fontSize: "14px", marginBottom: "20px" }}
          >
            {error}
          </div>
          <button
            onClick={() => fetchAllDashboardData()}
            style={{
              background: "#355CDE",
              color: "white",
              border: "none",
              padding: "10px 24px",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "#f3f4f6",
        minHeight: "100vh",
        padding: "16px",
        fontFamily: "Inter, sans-serif",
        fontSize: "13px",
      }}
    >
      <Container fluid>
        {/* TOP CARDS */}
        <Row className="g-3 mb-4">
          {stats.map((item, index) => (
            <Col lg={3} md={6} key={index}>
              <Card
                style={{
                  border: "none",
                  borderRadius: "16px",
                  overflow: "hidden",
                  background: item.bg,
                  color: "#fff",
                  boxShadow: `0 8px 20px ${item.shadow}`,
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    width: "100px",
                    height: "100px",
                    background: "rgba(255,255,255,0.08)",
                    borderRadius: "50%",
                    top: "-30px",
                    right: "-30px",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    width: "80px",
                    height: "80px",
                    background: "rgba(255,255,255,0.05)",
                    borderRadius: "50%",
                    bottom: "-30px",
                    right: "20px",
                  }}
                />
                <Card.Body
                  style={{
                    padding: "20px",
                    position: "relative",
                    zIndex: 2,
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          marginBottom: "8px",
                          opacity: 0.95,
                          letterSpacing: "0.3px",
                        }}
                      >
                        {item.title}
                      </h6>
                      <h2
                        style={{
                          fontSize: "28px",
                          fontWeight: "700",
                          marginBottom: "6px",
                          lineHeight: 1.2,
                        }}
                      >
                        {item.value}
                      </h2>
                      <div
                        style={{
                          fontSize: "11px",
                          fontWeight: "500",
                          opacity: 0.9,
                        }}
                      >
                        {item.change}
                      </div>
                    </div>
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "14px",
                        background: "rgba(255,255,255,0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "20px",
                      }}
                    >
                      {item.icon}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* SALES ANALYTICS */}
        {role !== "superadmin" && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3
                style={{
                  fontWeight: "700",
                  fontSize: "18px",
                  color: "#111827",
                }}
              >
                Sales Analytics
              </h3>

              <ButtonGroup
                style={{
                  background: "#fff",
                  borderRadius: "12px",
                  padding: "3px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                }}
              >
                <Button
                  onClick={() => setView("weekly")}
                  style={{
                    background: view === "weekly" ? "#fff" : "transparent",
                    border: "none",
                    color: view === "weekly" ? "#111827" : "#64748b",
                    fontWeight: "600",
                    padding: "6px 14px",
                    borderRadius: "10px",
                    fontSize: "12px",
                  }}
                >
                  Weekly
                </Button>
                <Button
                  onClick={() => setView("monthly")}
                  style={{
                    background: view === "monthly" ? "#fff" : "transparent",
                    border: "none",
                    color: view === "monthly" ? "#111827" : "#64748b",
                    fontWeight: "600",
                    padding: "6px 14px",
                    borderRadius: "10px",
                    fontSize: "12px",
                  }}
                >
                  Monthly
                </Button>
              </ButtonGroup>
            </div>

            <Card
              style={{
                border: "none",
                borderRadius: "20px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                marginBottom: "24px",
              }}
            >
              <Card.Body style={{ padding: "24px" }}>
                <h4
                  style={{
                    fontWeight: "700",
                    marginBottom: "16px",
                    color: "#111827",
                    fontSize: "16px",
                  }}
                >
                  {view === "weekly"
                    ? "This Week's Performance"
                    : "Monthly Performance"}
                </h4>

                <div style={{ width: "100%", height: 340 }}>
                  <ResponsiveContainer>
                    <AreaChart data={displayData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient
                          id="salesGradient"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#355CDE"
                            stopOpacity={0.35}
                          />
                          <stop
                            offset="95%"
                            stopColor="#355CDE"
                            stopOpacity={0}
                          />
                        </linearGradient>
                        <linearGradient
                          id="taxGradient"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#10C98F"
                            stopOpacity={0.25}
                          />
                          <stop
                            offset="95%"
                            stopColor="#10C98F"
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>

                      <CartesianGrid strokeDasharray="4 4" stroke="#dbe3ef" />
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        domain={[0, yAxisMax]}
                        tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
                        tick={{ fontSize: 11 }}
                        width={50}
                      />
                      <Tooltip 
                        contentStyle={{ fontSize: "12px", borderRadius: "8px" }}
                        formatter={(value, name) => {
                          if (name === "sales") return [`₹${value.toLocaleString()}`, "Sales"];
                          if (name === "tax") return [`₹${value.toLocaleString()}`, "Tax"];
                          return [value, name];
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="sales"
                        stroke="#355CDE"
                        strokeWidth={2.5}
                        fill="url(#salesGradient)"
                        name="sales"
                      />
                      <Area
                        type="monotone"
                        dataKey="tax"
                        stroke="#10C98F"
                        strokeWidth={2.5}
                        fill="url(#taxGradient)"
                        name="tax"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Card.Body>
            </Card>

            {/* BOTTOM SECTION */}
            <Row className="g-3">
              <Col lg={6}>
                <Card
                  style={{
                    border: "none",
                    borderRadius: "20px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                    height: "100%",
                  }}
                >
                  <Card.Body style={{ padding: "20px" }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h4
                        style={{
                          fontWeight: "700",
                          color: "#111827",
                          fontSize: "16px",
                          margin: 0,
                        }}
                      >
                        Recent Invoices
                      </h4>
                      <span
                        style={{
                          color: "#355CDE",
                          fontWeight: "600",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                      >
                        View all
                      </span>
                    </div>

                    {invoices.length > 0 ? (
                      invoices.map((item, index) => (
                        <div
                          key={index}
                          style={{
                            border: "1px solid #E5E7EB",
                            borderRadius: "16px",
                            padding: "14px",
                            marginBottom: "14px",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <h5
                                  style={{
                                    margin: 0,
                                    fontWeight: "700",
                                    color: "#111827",
                                    fontSize: "14px",
                                  }}
                                >
                                  {item.invoice}
                                </h5>
                                <span
                                  style={{
                                    background: item.statusBg,
                                    color: item.statusColor,
                                    padding: "4px 10px",
                                    borderRadius: "999px",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                  }}
                                >
                                  {item.status}
                                </span>
                              </div>
                              <div
                                style={{
                                  color: "#64748B",
                                  fontSize: "13px",
                                }}
                              >
                                {item.company}
                              </div>
                            </div>
                            <div className="text-end">
                              <h4
                                style={{
                                  fontWeight: "700",
                                  color: "#111827",
                                  marginBottom: "4px",
                                  fontSize: "16px",
                                }}
                              >
                                {item.amount}
                              </h4>
                              <div
                                style={{
                                  color: "#64748B",
                                  fontSize: "12px",
                                }}
                              >
                                {item.date}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: "#64748B",
                        }}
                      >
                        No recent invoices found
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={6}>
                <Row className="g-3">
                  <Col lg={12}>
                    <Card
                      style={{
                        border: "none",
                        borderRadius: "20px",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                      }}
                    >
                      <Card.Body style={{ padding: "20px" }}>
                        <h4
                          style={{
                            fontWeight: "700",
                            color: "#111827",
                            marginBottom: "16px",
                            fontSize: "16px",
                          }}
                        >
                          Customer Overview
                        </h4>

                        {customers.total > 0 ? (
                          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                            <div
                              style={{
                                width: "140px",
                                height: "140px",
                                borderRadius: "50%",
                                background: `conic-gradient(#10C98F 0% ${(customers.active / customers.total) * 100}%, #EF4444 ${(customers.active / customers.total) * 100}% 100%)`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <div
                                style={{
                                  width: "75px",
                                  height: "75px",
                                  borderRadius: "50%",
                                  background: "#fff",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "18px",
                                    fontWeight: "bold",
                                    color: "#1a56db",
                                  }}
                                >
                                  {Math.round((customers.active / customers.total) * 100)}%
                                </span>
                                <span style={{ fontSize: "10px", color: "#64748B" }}>
                                  Active
                                </span>
                              </div>
                            </div>

                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  background: "#F8FAFC",
                                  borderRadius: "14px",
                                  padding: "16px",
                                  marginBottom: "16px",
                                }}
                              >
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <span className="text-muted small">
                                      Total Customers
                                    </span>
                                    <h3 className="fw-bold mb-0 text-primary">
                                      {customers.total}
                                    </h3>
                                  </div>
                                  <div>
                                    <span className="text-muted small">
                                      Active Rate
                                    </span>
                                    <h3 className="fw-bold mb-0 text-success">
                                      {Math.round((customers.active / customers.total) * 100)}%
                                    </h3>
                                  </div>
                                </div>
                              </div>

                              <div className="d-flex gap-3">
                                <div
                                  style={{
                                    flex: 1,
                                    border: "1px solid #E5E7EB",
                                    borderRadius: "14px",
                                    padding: "14px",
                                    backgroundColor: "#ECFDF5",
                                  }}
                                >
                                  <h3
                                    style={{
                                      margin: 0,
                                      fontWeight: "700",
                                      fontSize: "18px",
                                      color: "#10B981",
                                    }}
                                  >
                                    {customers.active}
                                  </h3>
                                  <div style={{ color: "#64748B", fontSize: "12px" }}>
                                    Active Customers
                                  </div>
                                </div>
                                <div
                                  style={{
                                    flex: 1,
                                    border: "1px solid #E5E7EB",
                                    borderRadius: "14px",
                                    padding: "14px",
                                    backgroundColor: "#FEF2F2",
                                  }}
                                >
                                  <h3
                                    style={{
                                      margin: 0,
                                      fontWeight: "700",
                                      fontSize: "18px",
                                      color: "#EF4444",
                                    }}
                                  >
                                    {customers.inactive}
                                  </h3>
                                  <div style={{ color: "#64748B", fontSize: "12px" }}>
                                    Inactive Customers
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div style={{ textAlign: "center", padding: "20px", color: "#64748B" }}>
                            No customer data available
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col lg={12}>
                    <Card
                      style={{
                        border: "none",
                        borderRadius: "20px",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                      }}
                    >
                      <Card.Body style={{ padding: "20px" }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h4
                            style={{
                              fontWeight: "700",
                              color: "#111827",
                              fontSize: "16px",
                              margin: 0,
                            }}
                          >
                            Expense Summary
                          </h4>
                          <div className="text-end">
                            <span className="text-muted small">
                              Total Expenses
                            </span>
                            <h2
                              style={{
                                color: "#EF4444",
                                fontWeight: "700",
                                fontSize: "24px",
                                margin: 0,
                                lineHeight: 1.2,
                              }}
                            >
                              ₹{expenses.total.toLocaleString("en-IN")}
                            </h2>
                          </div>
                        </div>

                        {expenses.breakdown.length > 0 ? (
                          expenses.breakdown.map((item, index) => (
                            <div key={index} className="mb-2">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="small text-muted">
                                  {item.label}
                                </span>
                                <span className="small fw-semibold">
                                  ₹{item.amount.toLocaleString("en-IN")}
                                </span>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <div
                                  style={{
                                    width: "110px",
                                    textAlign: "right",
                                    color: "#64748B",
                                    fontSize: "12px",
                                  }}
                                >
                                  {item.width}
                                </div>
                                <div
                                  style={{
                                    flex: 1,
                                    height: "28px",
                                    background: "#F8FAFC",
                                    borderRadius: "6px",
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: item.width,
                                      height: "100%",
                                      background: "#F59E0B",
                                      borderRadius: "6px",
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div
                            style={{
                              textAlign: "center",
                              padding: "20px",
                              color: "#64748B",
                            }}
                          >
                            No expense data available
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </div>
  );
};

export default Dashboard;